import React from 'react';
import { Volume2, VolumeX } from 'lucide-react';

type Props = {
    /** 0–1, applied to every receiver `<video>` */
    volume: number;
    /** Combined: user mute, volume at 0, or autoplay policy blocked — drives icon */
    displayMuted: boolean;
    onVolumeChange: (volume: number) => void;
    onMutedToggle: () => void;
    /** When false, stream has no audio track — show disabled hint */
    hasAudioTrack: boolean;
    /** Show hint under controls (e.g. need tap / slider to unlock audio) */
    needsUserGesture?: boolean; 
};

/**
 * Master mute + volume for receiver screen share (Room applies to all video tiles).
 */
export const StreamAudioControl: React.FC<Props> = ({
    volume,
    displayMuted,
    onVolumeChange,
    onMutedToggle,
    hasAudioTrack,
    // needsUserGesture = false,
}) => {
    // When anything makes output silent (policy, user mute, etc.), show the thumb at 0 while
    // `volume` in parent can still hold the pre-mute level for restore on unmute / slider drag.
    const sliderValue = hasAudioTrack ? (displayMuted ? 0 : volume) : 0;
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-white/90">
                <button
                    type="button"
                    disabled={!hasAudioTrack}
                    onClick={() => {
                        if (!hasAudioTrack) return;
                        onMutedToggle();
                    }}
                    title={
                        !hasAudioTrack
                            ? 'No audio in this share'
                            : displayMuted
                              ? 'Unmute'
                              : 'Mute'
                    }
                    className="flex items-center justify-center rounded-lg p-2 transition-colors enabled:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {displayMuted || !hasAudioTrack ? (
                        <VolumeX className="h-4 w-4" />
                    ) : (
                        <Volume2 className="h-4 w-4" />
                    )}
                </button>
                <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    disabled={!hasAudioTrack}
                    value={sliderValue}
                    onChange={(e) => {
                        if (!hasAudioTrack) return;
                        const v = parseFloat(e.target.value);
                        onVolumeChange(v);
                    }}
                    className="h-1.5 w-24 cursor-pointer appearance-none rounded-lg bg-white/20 accent-white disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Share volume (all tiles)"
                />
                {!hasAudioTrack && (
                    <span
                        className="max-w-[140px] text-[11px] leading-tight text-white/45"
                        title="Sender did not share system/tab audio"
                    >
                        No share audio
                    </span>
                )}
            </div>
            {/* {hasAudioTrack && needsUserGesture && (
                <span className="max-w-[280px] pl-1 text-[11px] leading-snug text-amber-200/90">
                    Video plays without sound until you move the volume or tap Unmute.
                </span>
            )} */}
        </div>
    );
};
